import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import RosterPage from './pages/RosterPage';
import AttendancePage from './pages/AttendancePage';
import WeightPage from './pages/WeightPage';
import InjuriesPage from './pages/InjuriesPage';
import AlertsPage from './pages/AlertsPage';
import PlayerProfilePage from './pages/PlayerProfilePage';
import AthleteWeightKiosk from './pages/AthleteWeightKiosk';
import RehabKioskPage from './pages/RehabKioskPage';
import PendingApprovalPage from './pages/PendingApprovalPage';
import UserManagementPage from './pages/UserManagementPage';
import EquipmentPage from './pages/EquipmentPage';
import PracticeSchedulePage from './pages/PracticeSchedulePage';
import PracticeScheduleEditorPage from './pages/PracticeScheduleEditorPage';
import CoachCheckinPage from './pages/CoachCheckinPage';
import DepthChartPage from './pages/DepthChartPage';
import RehabCheckinPage from './pages/RehabCheckinPage';
import './App.css';

// Protected Route Component - checks for approval
function ProtectedRoute({ children }) {
  const { user, loading, isApproved } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#71717A]">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user is not approved, redirect to pending page
  if (!isApproved) {
    return <Navigate to="/pending-approval" replace />;
  }
  
  return children;
}

// Admin Route Component - only head_coach and trainer
function AdminRoute({ children }) {
  const { user, loading, isApproved, canApproveUsers } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#71717A]">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isApproved) {
    return <Navigate to="/pending-approval" replace />;
  }

  if (!canApproveUsers) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

// Public Route Component (redirect to dashboard if logged in)
function PublicRoute({ children }) {
  const { user, loading, isApproved } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#71717A]">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    if (!isApproved) {
      return <Navigate to="/pending-approval" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

// Pending Approval Route - only for unapproved users
function PendingRoute({ children }) {
  const { user, loading, isApproved } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#71717A]">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user is approved, redirect to dashboard
  if (isApproved) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Kiosk Routes - No Auth Required */}
      <Route path="/kiosk" element={<AthleteWeightKiosk />} />
      <Route path="/rehab-kiosk" element={<RehabKioskPage />} />
      
      {/* Coach Check-in Page - Protected but separate from main layout */}
      <Route 
        path="/coach-checkin" 
        element={
          <ProtectedRoute>
            <CoachCheckinPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Pending Approval Page */}
      <Route 
        path="/pending-approval" 
        element={
          <PendingRoute>
            <PendingApprovalPage />
          </PendingRoute>
        } 
      />
      
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/roster" 
        element={
          <ProtectedRoute>
            <RosterPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/player/:id" 
        element={
          <ProtectedRoute>
            <PlayerProfilePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/attendance" 
        element={
          <ProtectedRoute>
            <AttendancePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/weight" 
        element={
          <ProtectedRoute>
            <WeightPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/injuries" 
        element={
          <ProtectedRoute>
            <InjuriesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/alerts" 
        element={
          <ProtectedRoute>
            <AlertsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/users" 
        element={
          <AdminRoute>
            <UserManagementPage />
          </AdminRoute>
        } 
      />
      <Route 
        path="/equipment" 
        element={
          <ProtectedRoute>
            <EquipmentPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/practice" 
        element={
          <ProtectedRoute>
            <PracticeSchedulePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/practice/:id" 
        element={
          <ProtectedRoute>
            <PracticeScheduleEditorPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/depth-chart" 
        element={
          <ProtectedRoute>
            <DepthChartPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/rehab" 
        element={
          <ProtectedRoute>
            <RehabCheckinPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App noise-overlay">
          <AppRoutes />
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: '#121212',
                border: '1px solid #27272A',
                color: '#FFFFFF',
              },
            }}
          />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
